/**
 * Readers for the nested `{ id, display_string }` shape the search documents use
 * for a relation (`FieldMappings::nestedModelField`), e.g. a Live Track's artist
 * and album.
 *
 * One copy, because there were five: the same reader had been written out in
 * EntityListPage, TrackMatchingPage, LiveTrackSearchCardHeader,
 * useCurrentTrackSummaries and useCurrentTrackRow. They all narrowed to `number`
 * even where the surrounding code already typed ids as `string | number`, so the
 * day an id arrives as a string, four of the five would have had to be found and
 * changed — and missing one fails quietly: that surface's player line just goes
 * back to being plain text.
 */

/** The relation's id, or null when the value isn't a nested relation. */
export function readNestedId(value: unknown): number | string | null {
    if (!value || typeof value !== 'object' || !('id' in value)) {
        return null;
    }

    const id = (value as { id?: unknown }).id;
    if (typeof id === 'number' && Number.isFinite(id)) {
        return id;
    }

    return typeof id === 'string' && id.trim() !== '' ? id : null;
}

/** The relation's display string, trimmed, or null when it has none worth showing. */
export function readNestedDisplayString(value: unknown): string | null {
    if (!value || typeof value !== 'object' || !('display_string' in value)) {
        return null;
    }

    const display = (value as { display_string?: unknown }).display_string;

    return typeof display === 'string' && display.trim() !== '' ? display.trim() : null;
}

/**
 * A bucket id that names a real bucket, or null.
 *
 * A bucket with no vault stores `base_bucket_id = 0` rather than NULL (9,966 of
 * them in production), so 0 must read as "none". The backend's bucket-type
 * triggers use the same rule, `base_bucket_id > 0`, and this matches it so
 * nothing below 1 is ever taken for a bucket.
 */
export function readBucketId(value: unknown): number | string | null {
    if (typeof value === 'number') {
        return Number.isInteger(value) && value > 0 ? value : null;
    }
    if (typeof value === 'string' && /^\s*\d+\s*$/.test(value)) {
        return Number(value) > 0 ? value.trim() : null;
    }

    return null;
}
